/**
 * TikTok objective × optimization-goal × billing-event × bid-type matrix.
 *
 * MIRROR of the backend single source of truth at
 * `backend/src/modules/slighter-crm/tiktok/shared/tiktok-matrix.ts`.
 * Keep the two files in sync — the backend re-validates every field here and
 * rejects anything the frontend lets through that disagrees with it.
 *
 * Every row was LIVE-VERIFIED on 2026-08-27 against advertiser
 * 7637090698966728711 (PKR, PK) by creating paused ad groups through
 * /v1.3/adgroup/create/. TikTok's docs do not publish this matrix and its
 * error messages are cryptic ("Invalid optimization goal. Please change your
 * goal or promotion type."), so do NOT edit these values from intuition —
 * re-probe against the live API instead.
 *
 * Notable live findings baked in below:
 *  - `VIDEO_VIEW` is rejected outright by adgroup/create ("Unsupported
 *    feature"); the working video goals are ENGAGED_VIEW / ENGAGED_VIEW_FIFTEEN.
 *  - `LEAD`, `PROFILE_VISITS`, `ENGAGED_VIEW_FIFTEEN_SECONDS` are not valid
 *    enum members at all (correct: LEAD_GENERATION, PROFILE_VIEWS,
 *    ENGAGED_VIEW_FIFTEEN).
 *  - billing_event is NOT free choice: each goal accepts exactly one.
 *  - REACH additionally requires a frequency cap, CONVERT/VALUE require a
 *    pixel + a pixel event that already exists in Events Manager.
 *  - BID_TYPE_MAX_CONVERSION is rejected in every combination on this
 *    advertiser ("Invalid CPA smart bid type"), so it is not offered.
 */

export type TiktokObjective =
  | 'TRAFFIC' | 'VIDEO_VIEWS' | 'REACH' | 'ENGAGEMENT'
  | 'CONVERSIONS' | 'LEAD_GENERATION' | 'PRODUCT_SALES' | 'APP_PROMOTION';

export type TiktokBillingEvent = 'CPC' | 'CPM' | 'CPV' | 'OCPM';

export type TiktokBidType = 'BID_TYPE_NO_BID' | 'BID_TYPE_CUSTOM';

/** Extra inputs a goal needs before TikTok will accept the ad group or its ads. */
export type TiktokGoalRequirement =
  | 'PIXEL_EVENT' | 'FREQUENCY_CAP' | 'CATALOG' | 'APP' | 'LEAD_FORM' | 'APP_MMP';

/**
 * What the ADS under a goal point at.
 *  - 'required'  : landing page + CTA (the normal case)
 *  - 'forbidden' : TikTok rejects any CTA/landing page — FOLLOWERS ads drive to
 *                  the profile and only succeed with both fields absent
 *  - 'form'      : destination is an Instant Form (page_id), not a URL
 *  - 'app'       : destination is the app store listing (no landing page)
 */
export type TiktokAdDestination = 'required' | 'forbidden' | 'form' | 'app';

export interface TiktokGoalSpec {
  goal: string;
  label: string;
  /** The only billing_event TikTok accepts for this goal. */
  billingEvent: TiktokBillingEvent;
  requires: TiktokGoalRequirement[];
  /** Manual bid writes conversion_bid_price for OCPM goals, bid_price otherwise. */
  manualBidField: 'bid_price' | 'conversion_bid_price';
  /** What the ads under this goal point at. Defaults to 'required'. */
  adDestination?: TiktokAdDestination;
  hint: string;
}

export interface TiktokObjectiveSpec {
  objective: TiktokObjective;
  label: string;
  /** promotion_type sent on adgroup/create for this objective. */
  promotionType: string;
  goals: TiktokGoalSpec[];
  /**
   * Account-level asset the whole objective depends on. When set, the
   * objective is unusable until the advertiser has that asset — we surface it
   * rather than letting TikTok fail with "Could not find catalogue ID".
   */
  requiresAccountAsset?: 'CATALOG' | 'APP' | 'LEAD_FORM';
  unavailableReason?: string;
  /**
   * Selectable, but something will bite later — surfaced up-front so nobody
   * discovers it halfway through a build.
   */
  warning?: string;
  /** Two-or-three word pill on the objective tile, readable at a glance. */
  badge?: string;
}

const ocpm = (goal: string, label: string, hint: string, requires: TiktokGoalRequirement[] = []): TiktokGoalSpec =>
  ({ goal, label, billingEvent: 'OCPM', requires, manualBidField: 'conversion_bid_price', hint });

export const TIKTOK_MATRIX: TiktokObjectiveSpec[] = [
  {
    objective: 'TRAFFIC',
    label: 'Traffic',
    promotionType: 'WEBSITE',
    goals: [
      { goal: 'CLICK', label: 'Clicks', billingEvent: 'CPC', requires: [], manualBidField: 'bid_price',
        hint: 'Cheapest clicks to your link. Billed per click.' },
      ocpm('TRAFFIC_LANDING_PAGE_VIEW', 'Landing page views',
        'Optimises for people who actually load the page, not just tap. Billed per 1,000 impressions.'),
    ],
  },
  {
    objective: 'VIDEO_VIEWS',
    label: 'Video views',
    promotionType: 'WEBSITE',
    goals: [
      { goal: 'ENGAGED_VIEW', label: 'Engaged views (6s)', billingEvent: 'CPV', requires: [], manualBidField: 'bid_price',
        hint: 'Views of at least 6 seconds. Billed per view.' },
      { goal: 'ENGAGED_VIEW_FIFTEEN', label: 'Engaged views (15s)', billingEvent: 'CPV', requires: [], manualBidField: 'bid_price',
        hint: 'Views of at least 15 seconds — fewer, more committed viewers.' },
    ],
  },
  {
    objective: 'REACH',
    label: 'Reach',
    promotionType: 'WEBSITE',
    goals: [
      { goal: 'REACH', label: 'Unique reach', billingEvent: 'CPM', requires: ['FREQUENCY_CAP'], manualBidField: 'bid_price',
        hint: 'Maximise unique people reached. Requires a frequency cap.' },
    ],
  },
  {
    objective: 'ENGAGEMENT',
    label: 'Engagement',
    promotionType: 'WEBSITE',
    goals: [
      {
        ...ocpm('FOLLOWERS', 'Followers', 'Optimises for new profile followers. The ad drives to your TikTok profile, so it carries no link or call-to-action.'),
        adDestination: 'forbidden' as const,
      },
      { goal: 'PROFILE_VIEWS', label: 'Profile views', billingEvent: 'CPC', requires: [], manualBidField: 'bid_price',
        hint: 'Drives visits to your TikTok profile. Billed per click.' },
    ],
  },
  {
    objective: 'CONVERSIONS',
    label: 'Conversions',
    promotionType: 'WEBSITE',
    // Live-verified: with a pixel AND a configured event the ad group saves,
    // but /ad/create/ is then refused with "This campaign objective isn't
    // supported" for every identity/CTA combination — an advertiser permission
    // on TikTok's side.
    warning: 'TikTok is currently refusing ADS for this objective on this advertiser — the campaign and ad group save, but the ad is rejected. Install the TikTok pixel on your website so it receives real events, then retry.',
    badge: 'Not working yet',
    goals: [
      // A pixel + event is necessary for the ad group, but NOT sufficient for
      // the ad — see the objective-level warning above.
      { goal: 'CLICK', label: 'Clicks', billingEvent: 'CPC', requires: ['PIXEL_EVENT'], manualBidField: 'bid_price',
        hint: 'Optimises for clicks. A Conversions campaign still needs a pixel attached to the ad group.' },
      ocpm('CONVERT', 'Conversions', 'Optimises for a pixel event. Needs a pixel and a configured event.', ['PIXEL_EVENT']),
      // "Value isn't supported for current optimization event" — the pixel
      // event must itself carry a value/currency, not just exist.
      ocpm('VALUE', 'Value (ROAS)', 'Optimises for purchase value. Needs a pixel event that reports a value (e.g. a purchase event with currency).', ['PIXEL_EVENT']),
    ],
  },
  {
    objective: 'LEAD_GENERATION',
    label: 'Lead generation',
    promotionType: 'LEAD_GENERATION',
    // The Instant Form is chosen on the AD step, so the objective stays
    // selectable — hard-blocking it here went stale as soon as a form existed.
    // TikTok has no API to create forms; the ad step deep-links to Leads Center.
    goals: [
      {
        ...ocpm('LEAD_GENERATION', 'Leads', 'Optimises for instant-form lead submissions. The ad opens your Instant Form instead of a website.', ['LEAD_FORM']),
        adDestination: 'form' as const,
      },
    ],
  },
  {
    objective: 'PRODUCT_SALES',
    label: 'Product sales',
    promotionType: 'WEBSITE',
    requiresAccountAsset: 'CATALOG',
    badge: 'Not available',
    unavailableReason: 'Needs a product catalogue on the TikTok advertiser. Create one in TikTok Ads Manager → Assets → Catalogue first.',
    goals: [
      ocpm('CONVERT', 'Conversions', 'Optimises for catalogue purchases.', ['CATALOG']),
    ],
  },
  {
    objective: 'APP_PROMOTION',
    label: 'App promotion',
    promotionType: 'APP_ANDROID',
    // Live-verified with a registered app: ad groups need app_id +
    // operating_systems, and ads carry a CTA but no landing page. Install /
    // in-app-event optimisation needs a measurement partner on the app.
    warning: 'Install and in-app-event optimisation need app events set up (TikTok SDK or a measurement partner such as Adjust / Branch / AppsFlyer). Without them, only the Clicks goal is available.',
    badge: 'Clicks only',
    goals: [
      { goal: 'CLICK', label: 'Clicks to the app store', billingEvent: 'CPC', requires: ['APP'], manualBidField: 'bid_price',
        adDestination: 'app' as const,
        hint: 'Sends people to your app store listing. Works without a measurement partner.' },
      {
        ...ocpm('INSTALL', 'App installs', 'Optimises for installs. Requires a measurement partner on the app.', ['APP', 'APP_MMP']),
        adDestination: 'app' as const,
      },
      {
        ...ocpm('IN_APP_EVENT', 'In-app events', 'Optimises for a post-install event. Requires a measurement partner on the app.', ['APP', 'APP_MMP']),
        adDestination: 'app' as const,
      },
    ],
  },
];

const BY_OBJECTIVE = new Map<string, TiktokObjectiveSpec>(
  TIKTOK_MATRIX.map((o) => [o.objective, o]),
);

export function objectiveSpec(objective?: string): TiktokObjectiveSpec | undefined {
  return objective ? BY_OBJECTIVE.get(objective.toUpperCase()) : undefined;
}

export function goalsForObjective(objective?: string): TiktokGoalSpec[] {
  return objectiveSpec(objective)?.goals ?? [];
}

export function goalSpec(objective: string | undefined, goal: string): TiktokGoalSpec | undefined {
  return goalsForObjective(objective).find((g) => g.goal === goal.toUpperCase());
}

/** Default goal when the caller doesn't pick one — first valid goal for the objective. */
export function defaultGoalFor(objective?: string): string {
  return goalsForObjective(objective)[0]?.goal ?? 'CLICK';
}

/** The one billing_event TikTok accepts for this objective+goal pair. */
export function billingEventFor(objective: string | undefined, goal: string): TiktokBillingEvent {
  return goalSpec(objective, goal)?.billingEvent ?? 'CPC';
}

export function promotionTypeFor(objective?: string): string {
  return objectiveSpec(objective)?.promotionType ?? 'WEBSITE';
}

/** TikTok's own default frequency cap — REACH ad groups are rejected without one. */
export const DEFAULT_FREQUENCY_CAP = { frequency: 1, frequencySchedule: 7 };

/**
 * Where the ads under an objective+goal point. 'forbidden' means TikTok
 * rejects any CTA/landing page (FOLLOWERS); 'form' means the destination is an
 * Instant Form id rather than a URL (LEAD_GENERATION).
 */
export function adDestinationFor(objective: string | undefined, goal: string): TiktokAdDestination {
  return goalSpec(objective, goal)?.adDestination ?? 'required';
}
